import React from 'react'

import { ArrowLeft, Download, Info } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { WidgetId } from '../../Constants/WidgetConfig'
import { getWidgetDescription } from '../../Constants/WidgetDescriptions'
import {
  ChartDataPoint,
  ChartTypeSelector,
  renderChartByType,
} from '../ChartRenderer/ChartRenderer'

interface IChartWidgetProps {
  widgetId: WidgetId
  title: string
  data: ChartDataPoint[]
  chartName: string
  chartType: string
  isFlipped: boolean
  isDownloading: boolean
  onFlip: (widgetId: string, e: React.MouseEvent) => void
  onChangeChartType: (widgetId: string, chartType: string) => void
  onDownloadChart: (
    widgetId: string,
    widgetTitle: string,
    format: 'png' | 'jpeg',
  ) => void
  children?: React.ReactNode // For custom chart rendering (e.g., timeline with LineChart)
}

const ChartWidget: React.FC<IChartWidgetProps> = ({
  widgetId,
  title,
  data,
  chartName,
  chartType,
  isFlipped,
  isDownloading,
  onFlip,
  onChangeChartType,
  onDownloadChart,
  children,
}) => {
  const description = getWidgetDescription(widgetId)

  const content = (
    <div className="widget chart-widget" data-widget-id={widgetId}>
      <div className="widget-header">
        <span className="widget-title">{title}</span>
        <div className="widget-header-actions">
          <ChartTypeSelector
            widgetId={widgetId}
            currentType={chartType}
            onChangeChartType={onChangeChartType}
          />

          {/* Download button */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-export-exclude
              disabled={isDownloading}
              title={Localization.translator.dashboard.downloadChart}
              onMouseDown={e => e.stopPropagation()}
              render={<Button variant="ghost" size="icon" />}
            >
              {isDownloading ? <Spinner size="xs" /> : <Download />}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={isDownloading}
                onClick={() => onDownloadChart(widgetId, title, 'png')}
              >
                {Localization.translator.dashboard.downloadAsPng}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isDownloading}
                onClick={() => onDownloadChart(widgetId, title, 'jpeg')}
              >
                {Localization.translator.dashboard.downloadAsJpeg}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            data-export-exclude
            onClick={e => onFlip(widgetId, e)}
            onMouseDown={e => e.stopPropagation()}
            title={Localization.translator.dashboard.widgetInfo}
          >
            <Info />
          </Button>
        </div>
      </div>
      <div className="widget-body">
        {children ||
          renderChartByType({
            chartType,
            data,
            chartName,
          })}
      </div>
    </div>
  )

  return (
    <div className={`widget-flip-container ${isFlipped ? 'flipped' : ''}`}>
      <div className="widget-flip-inner">
        {/* Front side */}
        <div className="widget-flip-front">{content}</div>

        {/* Back side */}
        <div className="widget-flip-back">
          <div className="widget metric-widget">
            <div className="widget-header">
              <span className="widget-title">{title}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={e => onFlip(widgetId, e)}
                onMouseDown={e => e.stopPropagation()}
                title={Localization.translator.dashboard.backToWidget}
              >
                <ArrowLeft />
              </Button>
            </div>
            <div className="widget-body widget-description">
              <p>{description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChartWidget
